import { createCurrencySchema } from '@/entities/common'
import { z } from '@/utils/zod'

// The products that can be billed to the organization
export const OrganizationBillingProductSchema = z.enum([
    'main',
    'walletPasses',
    'outgoingMessagesArchive',
    'fileStorage',
])

export type OrganizationBillingProduct = z.infer<typeof OrganizationBillingProductSchema>

export const OrganizationBillingComissionTypeSchema = z.enum(['stripe', 'paypal'])

export type OrganizationBillingComissionType = z.infer<typeof OrganizationBillingComissionTypeSchema>

export const OrganizationBillingSubscriptionStatusSchema = z.enum([
    // The subscription is on trial. It's usually the first status of the subscription when created.
    'trialing',
    // The customer added a payment method and activated the subscription. No unpaid invoices.
    'active',
    // The customer has unpaid invoices but Stripe is still trying to collect them.
    'pastDue',
    // The customer has unpaid invoices and Stripe has given up on collecting them.
    'unpaid',
    // The customer has requested to cancel the subscription, but the cancelation date hasn't been reached yet.
    'willCancel',
    // The subscription has been canceled. This can happen when:
    // - The customer has requested to cancel the subscription
    // - The customer has not added a payment method and the trial has ended
    // - The customer has activated the subscription but the first payment failed and can't be recovered
    'canceled',
])

export type OrganizationBillingSubscriptionStatus = z.infer<typeof OrganizationBillingSubscriptionStatusSchema>

export const OrganizationBillingSubscriptionCancelReasonSchema = z.object({
    // Most of the reasons come from Stripe's built-in reasons. The exceptions are:
    // - trial_expired: The trial has ended and the subscription was canceled by Stripe
    // - first_payment_failed: The first payment failed and the subscription was voided by Stripe
    reason: z.enum([
        'other',
        'customer_service',
        'low_quality',
        'missing_features',
        'switched_service',
        'too_complex',
        'too_expensive',
        'unused',
        'trial_expired',
        'first_payment_failed',
    ]),
    comment: z.string(),
})

export type OrganizationBillingSubscriptionCancelReason = z.infer<
    typeof OrganizationBillingSubscriptionCancelReasonSchema
>

export const OrganizationBillingSubscriptionCancelBehaviourSchema = z.enum(['lockOrganization', 'deleteOrganization'])

export type OrganizationBillingSubscriptionCancelBehaviour = z.infer<
    typeof OrganizationBillingSubscriptionCancelBehaviourSchema
>

export const OrganizationBillingSubscriptionSchema = z.object({
    // Subscription ID in Stripe
    id: z.string(),
    // The status of the subscription. It affects the access to the organization
    status: OrganizationBillingSubscriptionStatusSchema,
    // If the subscription is a customized subscription.
    isCustom: z.boolean(),
    // The date when the subscription started
    startDate: z.isoDateTime,
    // The date when the trial ended/will end
    trialEndDate: z.isoDateTime.nullable(),
    // The date when the subscription will be canceled/was canceled
    cancelDate: z.isoDateTime.nullable(),
    // The reason why the subscription was/will be canceled
    cancelReason: OrganizationBillingSubscriptionCancelReasonSchema.nullable(),
    // The behaviour to apply when the subscription is canceled. It can be:
    // - lockOrganization: Lock the organization's access to the admin app and public apps
    // - deleteOrganization: Delete the organization. It's done when the user deletes the organization before the month is up.
    cancelBehaviour: OrganizationBillingSubscriptionCancelBehaviourSchema,
})

export type OrganizationBillingSubscription = z.infer<typeof OrganizationBillingSubscriptionSchema>

export const OrganizationBillingAccessLockSchema = z.object({
    // Whether the organization is at all locked
    isLocked: z.boolean(),
    // When access to the admin app was locked
    adminLockedAt: z.isoDateTime.nullable(),
    // When access to all public apps was locked. It happens two weeks after the admin is locked.
    publicLockedAt: z.isoDateTime.nullable(),
    // When the organization will be deleted. It happens three months after the admin is locked.
    deletionAt: z.isoDateTime.nullable(),
})

export type OrganizationBillingAccessLock = z.infer<typeof OrganizationBillingAccessLockSchema>

export const OrganizationBillingRevenueSchema = z.object({
    // How much this organization has spent in cents
    lifetimeValue: createCurrencySchema({ canBeNegative: false, canBeZero: true }),
    // How much this organization is currently spending monthly in cents
    currentMrr: createCurrencySchema({ canBeNegative: false, canBeZero: true }),
    // How much this organization has paid in standalone payments in cents
    standalonePayments: createCurrencySchema({ canBeNegative: false, canBeZero: true }),
    // How much this organization has paid in commissions in cents
    commissions: z
        .record(OrganizationBillingComissionTypeSchema, createCurrencySchema({ canBeNegative: false, canBeZero: true }))
        .default({ stripe: 0, paypal: 0 }),
})

export const OrganizationBillingSchema = z.object({
    // The ID of the customer in Stripe
    stripeCustomerId: z.string(),
    // The subscription details
    subscription: OrganizationBillingSubscriptionSchema,
    // The products that the organization currently uses
    products: z.array(OrganizationBillingProductSchema),
    // The lock status of the organization's access
    // This is used to prevent the organization from being accessed by the public or by the admin based on the billing status
    accessLock: OrganizationBillingAccessLockSchema,
    // The revenue generated by the organization
    revenue: OrganizationBillingRevenueSchema,
})

export type OrganizationBilling = z.infer<typeof OrganizationBillingSchema>
